import React, { useCallback } from 'react'
import { noop } from 'ramda-adjunct'

import { determineSchoolRender } from './utils/schoolRenderController.helpers'
import {
  SchoolRenderControllerState,
  UseSchoolRenderControllerContextReturn,
} from './SchoolRenderController.types'

type SchoolRenderControllerContext = UseSchoolRenderControllerContextReturn & {
  getRenderControllerState: () => SchoolRenderControllerState
  setRenderControllerStateFn: (
    updatedStateValues: Partial<SchoolRenderControllerState>
  ) => void
}

export const useSchoolRenderControllerContext =
  (): SchoolRenderControllerContext => {
    return {
      school: {
        getSchoolComponentsToRender: determineSchoolRender,
      },
      getRenderControllerState: () => ({
        school: {
          shouldRenderPlayers: false,
          shouldRenderAwards: false,
          shouldRenderUpdates: false,
        },
      }),
      setRenderControllerStateFn: noop,
    }
  }

export const schoolRenderControllerContext =
  React.createContext<SchoolRenderControllerContext>({
    school: {
      getSchoolComponentsToRender: () => ({
        shouldRenderPlayers: false,
        shouldRenderAwards: false,
        shouldRenderUpdates: false,
      }),
    },
    getRenderControllerState: () => ({
      school: {
        shouldRenderPlayers: false,
        shouldRenderAwards: false,
        shouldRenderUpdates: false,
      },
    }),
    setRenderControllerStateFn: noop,
  })

export const SchoolRenderControllerProvider: React.FC = ({ children }) => {
  const [renderControllerState, setRenderControllerState] =
    React.useState<SchoolRenderControllerState>({
      school: {
        shouldRenderPlayers: false,
        shouldRenderAwards: false,
        shouldRenderUpdates: false,
      },
    })

  const getRenderControllerState =
    useCallback((): SchoolRenderControllerState => {
      return renderControllerState
    }, [renderControllerState])

  const setRenderControllerStateFn = useCallback(
    (newState: Partial<SchoolRenderControllerState>): void => {
      setRenderControllerState((prevState) => ({
        ...prevState,
        ...newState,
      }))
    },
    []
  )

  const values = useSchoolRenderControllerContext()
  return (
    <schoolRenderControllerContext.Provider
      value={{
        ...values,
        getRenderControllerState,
        setRenderControllerStateFn,
      }}
    >
      {children}
    </schoolRenderControllerContext.Provider>
  )
}

export const useSchoolRenderControllerState = (): {
  getRenderControllerState: () => SchoolRenderControllerState
  setRenderControllerStateFn: (
    updatedStateValues: Partial<SchoolRenderControllerState>
  ) => void
} => {
  const { getRenderControllerState, setRenderControllerStateFn } =
    React.useContext(schoolRenderControllerContext)

  return { getRenderControllerState, setRenderControllerStateFn }
}
